/**
 * A radio group with two presentations.
 *
 *   pill  -- compact, substantial options laid out in a row. Used for duration.
 *   card  -- comparable side-by-side choices with a supporting line. Used for the
 *            mining mode.
 *
 * Both are real radio groups: a visually hidden native input drives each option, so
 * arrow-key navigation, screen-reader announcement and form semantics all behave the way
 * a user expects. The styling never replaces the semantics.
 */

export interface SegmentedOption<T extends string | number> {
  value: T;
  label: string;
  /**
   * The supporting line beneath the label.
   *
   * A NODE, NOT A STRING, so a word inside the sentence can be a link. It renders
   * outside the <label>'s accessible name, so an anchor here neither hijacks the click
   * that selects the radio nor lands in what a screen reader announces for the option.
   */
  hint?: React.ReactNode;
  /**
   * Extra content for the card, rendered inside its border but OUTSIDE the label.
   *
   * That placement is the whole point: anything interactive -- a link, typically --
   * cannot live inside the <label> without hijacking its click and misreporting the
   * label to assistive technology. `card` variant only; a pill has no room for it.
   */
  footer?: React.ReactNode;
}

interface SegmentedControlProps<T extends string | number> {
  legend: string;
  name: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  variant?: 'pill' | 'card';
}

export default function SegmentedControl<T extends string | number>({
  legend,
  name,
  options,
  value,
  onChange,
  variant = 'pill',
}: SegmentedControlProps<T>) {
  const isCard = variant === 'card';

  /*
   * Whether this control carries hints at all, decided once for the whole group.
   *
   * Per-option would be wrong: if one duration were at the base rate and the others were
   * not, only some pills would reserve the line and the row would sit unevenly. The
   * group is uniform or it has no hints.
   */
  const hasHints = options.some((option) => option.hint !== undefined && option.hint !== null);

  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="sr-only">{legend}</legend>

      <div
        className={
          isCard
            ? 'grid gap-3 sm:grid-cols-2'
            : 'grid grid-cols-2 gap-3 sm:grid-cols-4'
        }
        role="radiogroup"
        aria-label={legend}
      >
        {options.map((option) => {
          const selected = option.value === value;
          const id = `${name}-${option.value}`;

          const control = (
            <label
              key={String(option.value)}
              htmlFor={id}
              data-testid={id}
              data-selected={selected}
              className={
                isCard
                  ? // A card whose supporting line sits outside the label gives up its
                    // bottom padding; the support block supplies the rhythm instead.
                    `mm-choice${option.hint ? ' mm-choice--flush' : ''}`
                  : 'mm-pill'
              }
            >
              <input
                id={id}
                type="radio"
                name={name}
                className="sr-only"
                checked={selected}
                onChange={() => onChange(option.value)}
              />

              {isCard ? (
                /*
                  THE CARD'S LABEL IS THE TITLE, AND NOTHING ELSE.

                  The supporting line used to live in here. It moved out so it can carry
                  a LINK: a <label> may not contain interactive content other than its
                  own control, so an anchor inside one is invalid HTML -- it hijacks the
                  click that selects the radio and folds the link text into the option's
                  accessible name.

                  Moving it also shortens that name to just the option, which is what a
                  screen-reader user wants announced when arrowing through a radio group.
                  It renders immediately below, inside the same border, with the same
                  spacing it had -- see .mm-choice--flush and .mm-choice-support.
                */
                <span className="mm-choice-title">{option.label}</span>
              ) : (
                <>
                  <span className="mm-pill-label">{option.label}</span>
                  {/*
                    THE HINT SLOT IS ALWAYS RENDERED WHEN THE CONTROL USES HINTS, even
                    while its text is momentarily unknown. A hint that appeared and
                    disappeared would change the pill's height on every re-quote, which
                    is exactly the layout jump this page was reported for. An empty slot
                    holds its line; only the text inside it changes.
                  */}
                  {hasHints && (
                    <span className="mm-pill-hint">{option.hint ?? ''}</span>
                  )}
                </>
              )}
            </label>
          );

          /*
           * A pill, or a card with nothing extra, is the label alone -- unchanged.
           * A card WITH a footer gets the shell, which carries the border and the
           * selected state so both the label and the footer sit inside one box.
           */
          if (!isCard) return control;

          return (
            <div
              key={String(option.value)}
              className="mm-choice-shell"
              data-selected={selected}
            >
              {control}
              {/* Outside the label, so a word in this sentence may be a link. */}
              {option.hint && <div className="mm-choice-support">{option.hint}</div>}
              {option.footer && <div className="mm-choice-footer">{option.footer}</div>}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
